import { userManagementApi } from './userManagementService';

// Types for Inventory
export interface InventoryItem {
  id: string;
  name: string;
  code: string;
  description: string;
  category: string;
  type: 'consumable' | 'equipment' | 'medication' | 'supply';
  unit: string;
  currentStock: number;
  minStock: number;
  maxStock: number;
  cost: number;
  sellingPrice: number;
  supplier: string;
  batchNumber?: string;
  expiryDate?: string;
  location: string;
  isActive: boolean;
  autoConsumption: boolean;
  fifoEnabled: boolean;
  fefoEnabled: boolean;
  temperatureControlled: boolean;
  storageTemperature?: string;
  serialized: boolean;
  serialNumbers?: string[];
  createdAt: string;
  updatedAt: string;
  clinicId: string;
}

export interface InventoryTransaction {
  id: string;
  clinic_id: string;
  product_id: string;
  visit_id?: string;
  prescription_id?: string;
  procedure_id?: string;
  transaction_type: string;
  quantity: number;
  balance_after: number;
  created_by: string;
  created_at: string;
}

export interface InventoryBatch {
  id: string;
  clinic_id: string;
  product_id: string;
  batch_number: string;
  expiration_date: string;
  quantity_initial: number;
  quantity_available: number;
  unit_cost: number;
  is_expired: boolean;
  created_at: string;
  updated_at: string;
}

export interface StockMovement {
  id: string;
  itemId: string;
  type: 'in' | 'out' | 'adjustment' | 'transfer';
  quantity: number;
  reason: string;
  reference?: string; // appointment ID, supplier invoice, etc.
  performedBy: string;
  performedAt: string;
  notes?: string;
  cost?: number;
  location?: string;
}

export interface Supplier {
  id: string;
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  address: string;
  paymentTerms: string;
  isActive: boolean;
  clinicId: string;
}

export interface InventoryFilters {
  search?: string;
  category?: string;
  type?: string;
  stockStatus?: 'low' | 'out' | 'normal' | 'overstock';
  supplier?: string;
  location?: string;
  isActive?: boolean;
  clinicId?: string;
}

export interface CreateInventoryItemData {
  name: string;
  code: string;
  description: string;
  category: string;
  type: 'consumable' | 'equipment' | 'medication' | 'supply';
  unit: string;
  minStock: number;
  maxStock: number;
  cost: number;
  sellingPrice: number;
  supplier: string;
  location: string;
  autoConsumption: boolean;
  fifoEnabled: boolean;
  fefoEnabled: boolean;
  temperatureControlled: boolean;
  storageTemperature?: string;
  serialized: boolean;
  clinicId: string;
}

export interface UpdateInventoryItemData {
  name?: string;
  code?: string;
  description?: string;
  category?: string;
  type?: 'consumable' | 'equipment' | 'medication' | 'supply';
  unit?: string;
  minStock?: number;
  maxStock?: number;
  cost?: number;
  sellingPrice?: number;
  supplier?: string;
  location?: string;
  isActive?: boolean;
  autoConsumption?: boolean;
  fifoEnabled?: boolean;
  fefoEnabled?: boolean;
  temperatureControlled?: boolean;
  storageTemperature?: string;
  serialized?: boolean;
}

export interface InventoryStats {
  totalItems: number;
  activeItems: number;
  lowStockItems: number;
  outOfStockItems: number;
  totalValue: number;
  categories: { [key: string]: number };
  types: { [key: string]: number };
  topMovements: { itemId: string; name: string; quantity: number; type: string }[];
  alerts: InventoryAlert[];
}

export interface InventoryAlert {
  id: string;
  type: 'low_stock' | 'expired' | 'expiring_soon' | 'overstock';
  itemId: string;
  itemName: string;
  message: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  createdAt: string;
  acknowledged: boolean;
}

class InventoryService {
  // IMPORTANT: API_BASE_URL deja include /api/v1, deci aici folosim doar prefixul /inventory
  private baseUrl = '/inventory';

  /**
   * Lista produse din inventar (backend GET /inventory - format simplu)
   */
  async getInventoryList(params?: {
    page?: number;
    limit?: number;
    clinicId?: string;
    search?: string;
    category?: string;
  }): Promise<{ items: Array<Record<string, any>>; pagination: { page: number; limit: number; total: number } }> {
    try {
      const searchParams = new URLSearchParams();
      if (params?.page) searchParams.append('page', String(params.page));
      if (params?.limit) searchParams.append('limit', String(params.limit));
      if (params?.clinicId) searchParams.append('clinicId', params.clinicId);
      if (params?.search) searchParams.append('search', params.search);
      if (params?.category) searchParams.append('category', params.category);

      const response = await userManagementApi.get(`${this.baseUrl}?${searchParams.toString()}`);
      const d = response.data?.data ?? response.data;
      return {
        items: d?.items ?? [],
        pagination: d?.pagination ?? { page: 1, limit: 10, total: 0 },
      };
    } catch (error) {
      throw error;
    }
  }

  async getInventoryItems(filters: InventoryFilters = {}): Promise<{ items: InventoryItem[]; total: number; page: number; limit: number; stats: InventoryStats }> {
    try {
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.category) params.append('category', filters.category);
      if (filters.type) params.append('type', filters.type);
      if (filters.stockStatus) params.append('stockStatus', filters.stockStatus);
      if (filters.supplier) params.append('supplier', filters.supplier);
      if (filters.location) params.append('location', filters.location);
      if (filters.isActive !== undefined) params.append('isActive', filters.isActive.toString());
      if (filters.clinicId) params.append('clinicId', filters.clinicId);

      const response = await userManagementApi.get(`${this.baseUrl}?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getInventoryItemById(id: string): Promise<InventoryItem> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/${id}`);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw error;
    }
  }

  async createInventoryItem(data: CreateInventoryItemData): Promise<InventoryItem> {
    try {
      // Backend contract: name, description, category, quantity, min_quantity, unit_price, supplier
      const payload = {
        name: data.name,
        description: data.description,
        category: data.category,
        quantity: 0, // Initial stock is managed via batches/manual adjustments
        min_quantity: data.minStock,
        unit_price: data.cost,
        supplier: data.supplier,
      };
      const response = await userManagementApi.post(this.baseUrl, payload);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateInventoryItem(id: string, data: UpdateInventoryItemData): Promise<InventoryItem> {
    try {
      const payload = {
        name: data.name,
        description: data.description,
        category: data.category,
        min_quantity: data.minStock,
        unit_price: data.cost,
        supplier: data.supplier,
      };
      const response = await userManagementApi.put(`${this.baseUrl}/${id}`, payload);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteInventoryItem(id: string): Promise<void> {
    try {
      await userManagementApi.delete(`${this.baseUrl}/${id}`);
    } catch (error) {
      throw error;
    }
  }

  async getStockMovements(itemId?: string, filters: { from?: string; to?: string; type?: string } = {}): Promise<StockMovement[]> {
    try {
      const params = new URLSearchParams();
      
      if (itemId) params.append('itemId', itemId);
      if (filters.from) params.append('from', filters.from);
      if (filters.to) params.append('to', filters.to);
      if (filters.type) params.append('type', filters.type);

      const response = await userManagementApi.get(`${this.baseUrl}/movements?${params.toString()}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Verifica stoc disponibil pentru un produs (P0 Inventory)
   */
  async checkStock(productId: string, quantity: number, clinicId?: string): Promise<{
    available: number;
    requested: number;
    sufficient: boolean;
    productName?: string;
  }> {
    try {
      const params = new URLSearchParams({ productId, quantity: String(quantity) });
      if (clinicId) params.append('clinicId', clinicId);
      const response = await userManagementApi.get(`${this.baseUrl}/check-stock?${params.toString()}`);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Lista tranzactii inventar (audit P0)
   */
  async getTransactions(params?: {
    clinicId?: string;
    productId?: string;
    visitId?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ transactions: InventoryTransaction[]; total: number }> {
    try {
      const searchParams = new URLSearchParams();
      if (params?.clinicId) searchParams.append('clinicId', params.clinicId);
      if (params?.productId) searchParams.append('productId', params.productId);
      if (params?.visitId) searchParams.append('visitId', params.visitId);
      if (params?.limit) searchParams.append('limit', String(params.limit));
      if (params?.offset) searchParams.append('offset', String(params.offset));

      const response = await userManagementApi.get(`${this.baseUrl}/transactions?${searchParams.toString()}`);
      const d = response.data?.data ?? response.data;
      return { transactions: d?.transactions ?? [], total: d?.total ?? 0 };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Loturi de inventar (batches) pentru o clinica / produs.
   */
  async getBatches(params?: { clinicId?: string; productId?: string }): Promise<InventoryBatch[]> {
    try {
      const searchParams = new URLSearchParams();
      if (params?.clinicId) searchParams.append('clinicId', params.clinicId);
      if (params?.productId) searchParams.append('productId', params.productId);
      const query = searchParams.toString();
      const response = await userManagementApi.get(
        `${this.baseUrl}/batches${query ? `?${query}` : ''}`,
      );
      return response.data?.data ?? response.data ?? [];
    } catch (error) {
      throw error;
    }
  }

  async getExpiredBatches(params?: { clinicId?: string; productId?: string }): Promise<any[]> {
    try {
      const searchParams = new URLSearchParams();
      if (params?.clinicId) searchParams.append('clinicId', params.clinicId);
      if (params?.productId) searchParams.append('productId', params.productId);
      const query = searchParams.toString();
      const response = await userManagementApi.get(
        `${this.baseUrl}/reports/expired-batches${query ? `?${query}` : ''}`,
      );
      return response.data?.data ?? [];
    } catch (error) {
      throw error;
    }
  }

  async getExpiringBatches(params?: { clinicId?: string; productId?: string; days?: number }): Promise<any[]> {
    try {
      const searchParams = new URLSearchParams();
      if (params?.clinicId) searchParams.append('clinicId', params.clinicId);
      if (params?.productId) searchParams.append('productId', params.productId);
      if (params?.days) searchParams.append('days', String(params.days));
      const query = searchParams.toString();
      const response = await userManagementApi.get(
        `${this.baseUrl}/reports/expiring-batches${query ? `?${query}` : ''}`,
      );
      return response.data?.data ?? [];
    } catch (error) {
      throw error;
    }
  }

  async getStockDiscrepancies(params?: { clinicId?: string }): Promise<any[]> {
    try {
      const searchParams = new URLSearchParams();
      if (params?.clinicId) searchParams.append('clinicId', params.clinicId);
      const query = searchParams.toString();
      const response = await userManagementApi.get(
        `${this.baseUrl}/reports/stock-discrepancies${query ? `?${query}` : ''}`,
      );
      return response.data?.data ?? [];
    } catch (error) {
      throw error;
    }
  }

  /**
   * Receptie lot nou.
   */
  async createBatch(data: {
    productId: string;
    batchNumber: string;
    expirationDate: string;
    unitCost: number;
    quantity: number;
  }): Promise<InventoryBatch> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/batches`, data);
      return response.data?.data ?? response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Ajustare manuala stoc (doar admin)
   */
  async manualAdjust(productId: string, deltaQuantity: number, reason?: string): Promise<InventoryTransaction> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/${productId}/manual-adjust`, {
        deltaQuantity,
        reason,
      });
      return response.data?.data ?? response.data;
    } catch (error) {
      throw error;
    }
  }

  async createStockMovement(data: Omit<StockMovement, 'id' | 'performedAt'>): Promise<StockMovement> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/movements`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getSuppliers(clinicId: string): Promise<Supplier[]> {
    try {
      const response = await userManagementApi.get(`/suppliers?clinicId=${encodeURIComponent(clinicId)}`);
      const list = response.data?.data?.suppliers ?? response.data?.suppliers;
      return Array.isArray(list) ? list : [];
    } catch (error) {
      throw error;
    }
  }

  async createSupplier(data: Omit<Supplier, 'id'>): Promise<Supplier> {
    try {
      const response = await userManagementApi.post('/suppliers', data);
      return response.data?.data?.supplier ?? response.data;
    } catch (error) {
      throw error;
    }
  }

  async updateSupplier(id: string, data: Partial<Omit<Supplier, 'id' | 'clinicId'>>): Promise<Supplier> {
    try {
      const response = await userManagementApi.patch(`/suppliers/${id}`, data);
      return response.data?.data?.supplier ?? response.data;
    } catch (error) {
      throw error;
    }
  }

  async deleteSupplier(id: string): Promise<void> {
    try {
      await userManagementApi.delete(`/suppliers/${id}`);
    } catch (error) {
      throw error;
    }
  }

  async getInventoryStats(clinicId: string): Promise<InventoryStats> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/stats?clinicId=${clinicId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getInventoryAlerts(clinicId: string): Promise<InventoryAlert[]> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/alerts?clinicId=${clinicId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async acknowledgeAlert(alertId: string): Promise<void> {
    try {
      await userManagementApi.put(`${this.baseUrl}/alerts/${alertId}/acknowledge`);
    } catch (error) {
      throw error;
    }
  }

  async performInventoryCount(data: { itemId: string; countedQuantity: number; notes?: string }): Promise<StockMovement> {
    try {
      const response = await userManagementApi.post(`${this.baseUrl}/count`, data);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async exportInventory(filters: InventoryFilters = {}): Promise<Blob> {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(key, value.toString());
        }
      });

      const response = await userManagementApi.get(`${this.baseUrl}/export?${params.toString()}`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Descarca template Excel pentru import inventar.
   */
  async downloadImportTemplate(): Promise<Blob> {
    try {
      const response = await userManagementApi.get(`${this.baseUrl}/import-template`, {
        responseType: 'blob'
      } as any);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Importa inventar din fisier Excel.
   */
  async importFromFile(file: File): Promise<{ imported: number; skipped: number; errors: Array<{ row: number; message: string }> }> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await userManagementApi.post(`${this.baseUrl}/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const d = response.data?.data ?? response.data;
      return {
        imported: d?.imported ?? 0,
        skipped: d?.skipped ?? 0,
        errors: d?.errors ?? []
      };
    } catch (error) {
      throw error;
    }
  }
}

export const inventoryService = new InventoryService();
